import type { CityConfig } from "../city-template/types";

export const stPaulConfig: CityConfig = {
  city: "St. Paul",
  state: "Minnesota",
  stateAbbr: "MN",
  slug: "st-paul",
  route: "/cities/st-paul",
  title: "Emergency Passport St. Paul, MN — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in St. Paul, MN — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Ramsey County and Minnesota State Capitol area travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in St. Paul, MN",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help St. Paul and Ramsey County travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=St.%20Paul%20Minnesota%20skyline%20at%20dusk%20with%20the%20State%20Capitol%20dome%20illuminated%2C%20Mississippi%20River%20in%20the%20foreground%2C%20warm%20golden%20and%20amber%20tones%2C%20historic%20architecture%20mixed%20with%20modern%20buildings%2C%20professional%20cityscape%20photography%20with%20rich%20warm%20earth%20tones%2C%20no%20readable%20text%20or%20people%2C%20clean%20and%20inviting%20atmosphere&width=1800&height=700&seq=st-paul-hero-001&orientation=landscape",
  heroImageAlt: "St. Paul Minnesota skyline with illuminated State Capitol dome at dusk",
  geoPosition: "44.9537;-93.0900",
  geoPlacename: "St. Paul, Minnesota",
  lastModified: "2026-06-14",
  regionalAgency: {
    name: "Minneapolis Passport Agency",
    address: "212 3rd Ave S, Minneapolis, MN 55401",
    distance: "Minneapolis is 15–20 minutes from St. Paul via I-94 — our courier eliminates the need to drive",
  },
  nearbyCitiesZips: [
    { name: "Downtown St. Paul", zips: "55101–55102" },
    { name: "Summit Hill", zips: "55105" },
    { name: "Highland Park", zips: "55116" },
    { name: "Macalester-Groveland", zips: "55105" },
    { name: "Maplewood", zips: "55109" },
    { name: "Roseville", zips: "55113" },
    { name: "Woodbury", zips: "55125, 55129" },
    { name: "Eagan", zips: "55121, 55122" },
    { name: "Inver Grove Heights", zips: "55076, 55077" },
    { name: "South St. Paul", zips: "55075" },
    { name: "West St. Paul", zips: "55118" },
    { name: "Little Canada", zips: "55117" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent St. Paul departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Fast passport renewal in Minneapolis", route: "/cities/minneapolis" },
    { name: "Emergency passport help in Duluth", route: "/cities/duluth-mn" },
    { name: "Urgent passport services in Rochester", route: "/cities/rochester-mn" },
    { name: "Expedited passport services in Chicago", route: "/states/illinois" },
    { name: "Passport courier services in Iowa", route: "/states/iowa" },
    { name: "Rush passport help in Madison WI", route: "/states/illinois" },
  ],
  fpcOfficeAddress: "80 S 8th St, Minneapolis, MN 55402",
  fpcOfficeNote: "Fast Passport Center serves St. Paul remotely via secure courier. We do not have a walk-in office in St. Paul — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for St. Paul travelers.",
  fpcOfficeDistance: "Serving all of Ramsey County and the St. Paul metro",
  serviceAreaIntro: "Fast Passport Center provides emergency and fast passport renewal services throughout the entire St. Paul metro area. From downtown St. Paul to the eastern suburbs, from the State Capitol to the University of Minnesota St. Paul campus — we help Ramsey County travelers get their passports fast, often in 24–48 hours through our registered courier service.",
  nearbyCitiesIntro: "We serve all St. Paul neighborhoods and nearby suburbs including Maplewood, Roseville, Woodbury, Eagan, Inver Grove Heights, and West St. Paul. If you are in Ramsey County or the eastern Twin Cities metro, we have you covered.",
  howItWorksIntro: "Most St. Paul travelers complete everything online and ship documents overnight. No need to drive to Chicago or wait in line. Our emergency and expedited passport process is designed for Ramsey County travelers who need fast, reliable results.",
  mistakesIntro: "With MSP Airport just across the river, St. Paul travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "St. Paul travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in St. Paul? Here are the most common ones we hear from Ramsey County travelers.",
  statePageRoute: "/states/minnesota",
  statePageName: "Minnesota",
  dmvWebsite: "https://dps.mn.gov/divisions/dvs",
  dmvLabel: "Minnesota DVS — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent St. Paul international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "David L.",
      location: "Summit Hill, St. Paul, MN",
      rating: 5,
      text: "My flight to Paris was in 3 days and I realized my passport was expired. Fast Passport Center got me a new one in 36 hours. Absolutely saved my business trip.",
      service: "Emergency Passport",
    },
    {
      name: "Karen S.",
      location: "Woodbury, MN",
      rating: 5,
      text: "I needed to renew my passport for a family vacation to Mexico. The process was smooth, they reviewed my documents, and I had my passport in 4 days. Highly recommend.",
      service: "Passport Renewal",
    },
    {
      name: "Robert J.",
      location: "Highland Park, St. Paul, MN",
      rating: 5,
      text: "Lost my passport the week before my honeymoon to Greece. They walked me through the DS-64 process and got me a replacement in 2 days. Incredible service.",
      service: "Lost Passport",
    },
    {
      name: "Jennifer W.",
      location: "Eagan, MN",
      rating: 5,
      text: "As a state employee who travels internationally for conferences, I can't afford passport delays. Fast Passport Center has been my go-to for years. Professional and reliable.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "stp-1",
      name: "St. Paul Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "180 E Kellogg Blvd",
      city: "St. Paul",
      state: "MN",
      zip: "55101",
      phone: "(651) 291-1115",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.2 miles from downtown St. Paul",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "stp-2",
      name: "Maplewood Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2280 County Rd D E",
      city: "Maplewood",
      state: "MN",
      zip: "55109",
      phone: "(651) 770-8443",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "5 miles from downtown St. Paul",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Busy location — book appointments 2+ weeks in advance. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "stp-3",
      name: "Roseville Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2800 W County Rd C",
      city: "Roseville",
      state: "MN",
      zip: "55113",
      phone: "(651) 631-2473",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "7 miles from downtown St. Paul",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for north metro residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "stp-4",
      name: "Woodbury City Clerk",
      type: "City Clerk Passport Acceptance",
      address: "8301 Valley Creek Rd",
      city: "Woodbury",
      state: "MN",
      zip: "55125",
      phone: "(651) 714-3500",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "10 miles from downtown St. Paul",
      website: "https://www.woodburymn.gov/",
      websiteLabel: "Woodbury City Clerk",
      notes: "Accepts walk-ins but appointments preferred. Photo services available on-site.",
      icon: "ri-government-line",
    },
    {
      id: "stp-5",
      name: "Eagan Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1200 Diffley Rd",
      city: "Eagan",
      state: "MN",
      zip: "55123",
      phone: "(651) 452-9432",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "12 miles from downtown St. Paul",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for south metro residents. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "stp-6",
      name: "Ramsey County Service Center",
      type: "County Clerk Passport Acceptance",
      address: "160 E Kellogg Blvd",
      city: "St. Paul",
      state: "MN",
      zip: "55101",
      phone: "(651) 266-8020",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "0.3 miles from downtown St. Paul",
      website: "https://www.ramseycounty.us/",
      websiteLabel: "Ramsey County",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 15–20 minutes to Minneapolis", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "St. Paul photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at St. Paul acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many St. Paul applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many St. Paul families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "St. Paul travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in St. Paul." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your St. Paul address — trackable every step of the way." },
  ],
  faqs: [
    {
      q: "How fast can I get an emergency passport in St. Paul?",
      a: "Fast Passport Center offers emergency passport processing in St. Paul in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, bypassing standard mail delays. This is the fastest reliable option for Ramsey County travelers with urgent international flights.",
    },
    {
      q: "Can I get a same-day passport in St. Paul?",
      a: "Same-day passports are only available at regional passport agencies for documented life-or-death emergencies with proof of travel within 72 hours. The nearest agency to St. Paul is the Minneapolis Passport Agency at 212 3rd Ave S — about 15–20 minutes via I-94. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option available to St. Paul residents.",
    },
    {
      q: "Where can I get a fast passport renewal in St. Paul?",
      a: "Fast Passport Center offers fast passport renewal in St. Paul with no office visit required. We serve all Ramsey County neighborhoods including Summit Hill, Highland Park, Maplewood, Roseville, and Woodbury. Simply complete your application online, ship your documents using our prepaid overnight label, and our couriers handle the rest.",
    },
    {
      q: "What is the fastest way to get an urgent passport in St. Paul?",
      a: "The fastest way to get an urgent passport in St. Paul is through Fast Passport Center's emergency courier service. Our registered couriers deliver your documents directly to State Department processing facilities, achieving 24–48 hour turnaround. This is significantly faster than standard mail processing (8–11 weeks) or even government expedited mail (3–5 weeks).",
    },
    {
      q: "How fast can I get a passport in St. Paul?",
      a: "With Fast Passport Center's emergency courier service, St. Paul travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to visit a passport agency in person?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in St. Paul?",
      a: "Popular options include the St. Paul Main Post Office (55101), Maplewood Post Office (55109), Roseville Post Office (55113), and Ramsey County Service Center (55101). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "What if I live in the eastern suburbs like Woodbury or Eagan?",
      a: "We serve all of Ramsey County and the eastern Twin Cities metro including Woodbury, Eagan, Inver Grove Heights, and West St. Paul. Our overnight shipping labels work from any UPS location, so your location doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in St. Paul?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in St. Paul?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in St. Paul offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in St. Paul?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in St. Paul?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk St. Paul families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Highland Park", address: "740 Cleveland Ave S, St. Paul, MN 55116", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Downtown St. Paul", address: "125 Wabasha St S, St. Paul, MN 55107", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — St. Paul", address: "60 E 5th St, St. Paul, MN 55101", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Woodbury", address: "1785 Radio Dr, Woodbury, MN 55125", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Eagan", address: "1276 Town Centre Dr, Eagan, MN 55123", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Roseville", address: "2615 Snelling Ave N, Roseville, MN 55113", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Maplewood", address: "1750 Beam Ave, Maplewood, MN 55109", hours: "Mon–Sun: 7AM–11PM" },
    { name: "CVS Pharmacy — Inver Grove Heights", address: "5660 Bishop Ave, Inver Grove Heights, MN 55076", hours: "Mon–Sun: 8AM–10PM" },
  ],
  dmvLocations: [
    {
      name: "DMV — Downtown St. Paul",
      address: "445 Minnesota St, St. Paul, MN 55101",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — Eagan",
      address: "2935 Lexington Ave S, Eagan, MN 55121",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — Roseville",
      address: "2580 Hamline Ave N, Roseville, MN 55113",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — Woodbury",
      address: "1020 Woodlane Dr, Woodbury, MN 55125",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Full-service center. Extended hours available.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "MSP",
      name: "Minneapolis–Saint Paul International Airport",
      distance: "8 miles from downtown St. Paul",
      address: "4300 Glumack Dr, St. Paul, MN 55111",
      note: "The primary international airport for the Twin Cities. Major hub for international flights to Europe, Asia, and the Americas.",
      flightUrl: "https://www.mspairport.com/",
      flightLabel: "MSP Flight Info",
      featured: true,
    },
    {
      code: "STP",
      name: "St. Paul Downtown Airport",
      distance: "5 miles from downtown St. Paul",
      address: "644 Bayfield St, St. Paul, MN 55107",
      note: "General aviation airport. No commercial international flights.",
      flightUrl: "https://www.metroairports.org/",
      flightLabel: "St. Paul Downtown Airport",
      featured: false,
    },
  ],
  vitalRecordsIcon: "ri-file-text-line",
  vitalRecordsLinks: [
    {
      icon: "ri-user-smile-line",
      label: "Birth Certificate",
      desc: "Request certified birth certificates from the Minnesota Department of Health Vital Records. Online, mail, or in-person options available. Ramsey County also provides birth records for St. Paul residents.",
      url: "https://www.health.state.mn.us/people/vitalrecords/"
    },
    {
      icon: "ri-file-list-2-line",
      label: "Death Certificate",
      desc: "Obtain certified death certificates through the Minnesota Department of Health Vital Records. Required for estate, legal, and survivor benefit matters.",
      url: "https://www.health.state.mn.us/people/vitalrecords/"
    },
    {
      icon: "ri-hearts-line",
      label: "Marriage Certificate",
      desc: "Request certified marriage records from Ramsey County Vital Records. Required for name change passport applications. Serving St. Paul and all Ramsey County communities.",
      url: "https://www.ramseycounty.us/residents/records"
    },
    {
      icon: "ri-file-copy-line",
      label: "Divorce Decree",
      desc: "Obtain certified divorce records through the Ramsey County District Court. Required for name change passport corrections and legal documentation.",
      url: "https://www.mncourts.gov/"
    }
  ],
};